import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { 
  BarChart3, TrendingUp, TrendingDown, DollarSign, Users, 
  CreditCard, Shield, Download, Calendar, RefreshCw, PieChart, Home, ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ethers } from 'ethers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Reports = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { account, contracts } = useWeb3();
  
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30d');
  const [protocolMetrics, setProtocolMetrics] = useState({
    totalDeposited: '0',
    totalCreditIssued: '0',
    totalInterestCollected: '0',
    activePositions: 0,
    totalLiquidations: 0,
    liquidatedValue: '0'
  });
  const [marketData, setMarketData] = useState({
    ethPrice: 0,
    bethPrice: 1.00,
    ethChange24h: 0
  });

  const fetchProtocolMetrics = async () => {
    if (!contracts?.vault || !contracts?.credit) return;
    
    try {
      const [totalDeposited, totalCreditIssued, totalInterestCollected] = await Promise.all([
        contracts.vault.totalDeposited(),
        contracts.credit.totalCreditIssued(),
        contracts.credit.totalInterestCollected()
      ]);

      setProtocolMetrics({
        totalDeposited: ethers.utils.formatUnits(totalDeposited, 18),
        totalCreditIssued: ethers.utils.formatUnits(totalCreditIssued, 18),
        totalInterestCollected: ethers.utils.formatUnits(totalInterestCollected, 18),
        activePositions: 0,
        totalLiquidations: 0,
        liquidatedValue: '0'
      });
    } catch (error) {
      console.error('Error fetching protocol metrics:', error);
    }
  };

  const fetchMarketData = async () => {
    try {
      // Fetch ETH price from CoinGecko
      const response = await fetch(
        'https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd&include_24hr_change=true'
      );
      const data = await response.json();
      
      setMarketData({
        ethPrice: data.ethereum?.usd || 3500,
        ethChange24h: data.ethereum?.usd_24h_change || 0,
        bethPrice: 1.00 // BETH is newly deployed, no market price yet
      });
    } catch (error) {
      console.error('Error fetching market data:', error);
      // Fallback prices
      setMarketData({
        ethPrice: 3500,
        ethChange24h: 0,
        bethPrice: 1.00
      });
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    
    const fetchAllData = async () => {
      setLoading(true);
      await Promise.all([
        fetchProtocolMetrics(),
        fetchMarketData()
      ]);
      setLoading(false);
    };
    
    fetchAllData();
  }, [token, navigate, contracts]);

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([
      fetchProtocolMetrics(),
      fetchMarketData()
    ]);
    setLoading(false);
  };

  const formatNumber = (num) => {
    const n = parseFloat(num);
    if (n >= 1e12) return (n / 1e12).toFixed(2) + 'T';
    if (n >= 1e9) return (n / 1e9).toFixed(2) + 'B';
    if (n >= 1e6) return (n / 1e6).toFixed(2) + 'M';
    if (n >= 1e3) return (n / 1e3).toFixed(2) + 'K';
    return n.toFixed(2);
  };

  const formatUSD = (num) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  const calculateTVLInUSD = () => {
    const bethAmount = parseFloat(protocolMetrics.totalDeposited);
    return bethAmount * marketData.bethPrice;
  };

  const calculateRevenueInUSD = () => {
    const interestBeth = parseFloat(protocolMetrics.totalInterestCollected);
    return interestBeth * marketData.bethPrice;
  };

  const exportReport = () => {
    const report = {
      generatedAt: new Date().toISOString(),
      dateRange,
      marketData,
      protocolMetrics: {
        ...protocolMetrics,
        tvlUSD: calculateTVLInUSD(),
        revenueUSD: calculateRevenueInUSD()
      }
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bethereum_report_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    toast.success('Report exported');
  };

  const isOwner = account?.toLowerCase() === '0x2fb355b99b2060e42db50d99393d00f47a41d322'.toLowerCase();

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="reports-page">
      {/* Header */}
      <div className="bg-gray-900/50 border-b border-gray-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/admin-dashboard">
              <Button variant="outline" size="sm" className="border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
            <div className="h-8 w-px bg-gray-700" />
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <BarChart3 className="w-6 h-6" />
                Reports & Analytics
              </h1>
              <p className="text-gray-400 text-sm">Protocol metrics and financial reports</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={exportReport} className="bg-[#10b981]">
              <Download className="w-4 h-4 mr-2" />
              Export Report
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Date Range Filter */}
        <div className="flex gap-2 mb-6">
          {['24h', '7d', '30d', '90d', 'All'].map(range => (
            <Button
              key={range}
              variant={dateRange === range ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateRange(range)}
              className={dateRange === range ? 'bg-[#10b981]' : ''}
            >
              {range}
            </Button>
          ))}
        </div>

        {/* Market Prices */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-blue-900/30 to-blue-800/10 border-blue-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-blue-400 text-sm">ETH Price</span>
                <DollarSign className="w-4 h-4 text-blue-400" />
              </div>
              <p className="text-3xl font-bold text-white">{formatUSD(marketData.ethPrice)}</p>
              <p className={`text-sm ${marketData.ethChange24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {marketData.ethChange24h >= 0 ? <TrendingUp className="w-3 h-3 inline mr-1" /> : <TrendingDown className="w-3 h-3 inline mr-1" />}
                {marketData.ethChange24h.toFixed(2)}% (24h)
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-yellow-900/30 to-yellow-800/10 border-yellow-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-yellow-400 text-sm">BETH Price</span>
                <DollarSign className="w-4 h-4 text-yellow-400" />
              </div>
              <p className="text-3xl font-bold text-white">${marketData.bethPrice.toFixed(2)}</p>
              <p className="text-sm text-gray-400">Internal valuation</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-900/30 to-green-800/10 border-green-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-green-400 text-sm">Total Value Locked</span>
                <Shield className="w-4 h-4 text-green-400" />
              </div>
              <p className="text-3xl font-bold text-white">{formatUSD(calculateTVLInUSD())}</p>
              <p className="text-sm text-gray-400">{formatNumber(protocolMetrics.totalDeposited)} BETH</p>
            </CardContent>
          </Card>
        </div>

        {/* Protocol Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Total Deposited</span>
                <Shield className="w-4 h-4 text-blue-400" />
              </div>
              <p className="text-2xl font-bold text-white">{formatNumber(protocolMetrics.totalDeposited)}</p>
              <p className="text-xs text-gray-500">BETH in vault</p>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Credit Issued</span>
                <CreditCard className="w-4 h-4 text-green-400" />
              </div>
              <p className="text-2xl font-bold text-white">{formatNumber(protocolMetrics.totalCreditIssued)}</p>
              <p className="text-xs text-gray-500">BETH borrowed</p>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Interest Revenue</span>
                <DollarSign className="w-4 h-4 text-yellow-400" />
              </div>
              <p className="text-2xl font-bold text-white">{formatNumber(protocolMetrics.totalInterestCollected)}</p>
              <p className="text-xs text-gray-500">{formatUSD(calculateRevenueInUSD())}</p>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Utilization Rate</span>
                <PieChart className="w-4 h-4 text-purple-400" />
              </div>
              <p className="text-2xl font-bold text-white">
                {protocolMetrics.totalDeposited > 0 
                  ? ((parseFloat(protocolMetrics.totalCreditIssued) / parseFloat(protocolMetrics.totalDeposited)) * 100).toFixed(1)
                  : '0'}%
              </p>
              <p className="text-xs text-gray-500">Credit / Deposits</p>
            </CardContent>
          </Card>
        </div>

        {/* Revenue Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="bg-gray-900/50 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Revenue Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-gray-800/50 rounded">
                  <span className="text-gray-400">Interest Income</span>
                  <span className="text-white font-bold">{formatNumber(protocolMetrics.totalInterestCollected)} BETH</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-800/50 rounded">
                  <span className="text-gray-400">Withdrawal Fees (0.5%)</span>
                  <span className="text-white font-bold">— BETH</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-800/50 rounded">
                  <span className="text-gray-400">Liquidation Fees</span>
                  <span className="text-white font-bold">— BETH</span>
                </div>
                <hr className="border-gray-800" />
                <div className="flex justify-between items-center p-3 bg-[#10b981]/10 rounded border border-[#10b981]/30">
                  <span className="text-[#10b981] font-medium">Total Revenue</span>
                  <span className="text-[#10b981] font-bold">{formatNumber(protocolMetrics.totalInterestCollected)} BETH</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Protocol Health</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-400">Average LTV</span>
                    <span className="text-white">0%</span>
                  </div>
                  <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 rounded-full" style={{ width: '0%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-400">Utilization Rate</span>
                    <span className="text-white">
                      {protocolMetrics.totalDeposited > 0 
                        ? ((parseFloat(protocolMetrics.totalCreditIssued) / parseFloat(protocolMetrics.totalDeposited)) * 100).toFixed(1)
                        : '0'}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-500 rounded-full" 
                      style={{ 
                        width: `${protocolMetrics.totalDeposited > 0 
                          ? ((parseFloat(protocolMetrics.totalCreditIssued) / parseFloat(protocolMetrics.totalDeposited)) * 100)
                          : 0}%` 
                      }}
                    ></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-4">
                  <div className="text-center p-3 bg-gray-800/50 rounded">
                    <p className="text-2xl font-bold text-green-400">0</p>
                    <p className="text-xs text-gray-500">Liquidations</p>
                  </div>
                  <div className="text-center p-3 bg-gray-800/50 rounded">
                    <p className="text-2xl font-bold text-white">8%</p>
                    <p className="text-xs text-gray-500">Current APR</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info Notice */}
        <Card className="bg-yellow-900/10 border-yellow-500/30">
          <CardContent className="py-4">
            <p className="text-yellow-400 text-sm">
              <strong>Note:</strong> BETH price is set at $1.00 internal valuation. 
              To enable market pricing, create a liquidity pool on Uniswap or integrate with a price oracle.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Reports;
